import {
  workflowPipelineResponseSchema,
  workflowActionContextSchema,
  workflowActionRequestSchema,
  type WorkflowPipelineResource,
  type WorkflowActionContext,
  type WorkflowActionRequest,
  type WorkflowPipelineQuery,
  type WorkflowPipelineResponse,
  assignLeadRequestSchema,
  leadDetailSchema,
  leadFormOptionsResponseSchema,
  updateLeadStatusRequestSchema,
  type Lead,
  type LeadFormOptionsResponse,
} from "@crewfsm/contracts";

import type { PipelineStatus } from "./pipeline-model";

const APP_ID = "crewfsm.pipeline";

export interface PipelineApi {
  board(
    query: Partial<WorkflowPipelineQuery>,
  ): Promise<WorkflowPipelineResponse>;
  actionContext(
    resource: WorkflowPipelineResource,
    locationId: string,
  ): Promise<WorkflowActionContext>;
  act(
    resource: WorkflowPipelineResource,
    locationId: string,
    body: WorkflowActionRequest,
  ): Promise<WorkflowActionContext>;
  lead(leadId: string): Promise<Lead>;
  formOptions(): Promise<LeadFormOptionsResponse>;
  updateStatus(leadId: string, status: PipelineStatus): Promise<Lead>;
  assign(leadId: string, assignedToUserId: string | null): Promise<Lead>;
  archive(leadId: string): Promise<Lead>;
}

export interface PipelineApiSession {
  getSessionToken(): string;
  refreshSessionToken(): Promise<string>;
}

export function createPipelineApi(session: PipelineApiSession): PipelineApi {
  const baseUrl = resolveApiBaseUrl();
  let refreshPromise: Promise<string> | null = null;
  const refreshSessionToken = (): Promise<string> => {
    refreshPromise ??= session
      .refreshSessionToken()
      .finally(() => (refreshPromise = null));
    return refreshPromise;
  };
  const request = async (
    path: string,
    init?: RequestInit,
  ): Promise<unknown> => {
    let response = await send(path, session.getSessionToken(), init);
    if (response.status === 401) {
      response = await send(path, await refreshSessionToken(), init);
    }
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        code?: string;
        message?: string;
        error?: { message?: string };
      } | null;
      throw new PipelineApiError(
        response.status,
        payload?.message ??
          payload?.error?.message ??
          "CrewFSM could not complete this request.",
        payload?.code,
      );
    }
    return response.json();
  };

  const send = (
    path: string,
    sessionToken: string,
    init?: RequestInit,
  ): Promise<Response> =>
    fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        authorization: `Bearer ${sessionToken}`,
        "content-type": "application/json",
        "x-crew-app-id": APP_ID,
        ...init?.headers,
      },
    });

  return {
    async board(query) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== "") params.set(key, String(value));
      }
      return workflowPipelineResponseSchema.parse(
        await request(`/workflow-pipeline?${params}`),
      );
    },
    async actionContext(resource, locationId) {
      return workflowActionContextSchema.parse(
        await request(
          `/workflow-pipeline/actions/${resource.type}/${encodeURIComponent(resource.id)}?locationId=${encodeURIComponent(locationId)}`,
        ),
      );
    },
    async act(resource, locationId, input) {
      return workflowActionContextSchema.parse(
        await request(
          `/workflow-pipeline/actions/${resource.type}/${encodeURIComponent(resource.id)}?locationId=${encodeURIComponent(locationId)}`,
          {
            method: "POST",
            body: JSON.stringify(workflowActionRequestSchema.parse(input)),
          },
        ),
      );
    },
    async lead(leadId) {
      return leadDetailSchema.parse(
        await request(`/leads/${encodeURIComponent(leadId)}`),
      );
    },
    async formOptions() {
      return leadFormOptionsResponseSchema.parse(
        await request("/leads/form-options"),
      );
    },
    async updateStatus(leadId, status) {
      const body = updateLeadStatusRequestSchema.parse({ status });
      return leadDetailSchema.parse(
        await request(`/leads/${encodeURIComponent(leadId)}/status`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
      );
    },
    async assign(leadId, assignedToUserId) {
      const body = assignLeadRequestSchema.parse({ assignedToUserId });
      return leadDetailSchema.parse(
        await request(`/leads/${encodeURIComponent(leadId)}/assign`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
      );
    },
    async archive(leadId) {
      return leadDetailSchema.parse(
        await request(`/leads/${encodeURIComponent(leadId)}/archive`, {
          method: "POST",
        }),
      );
    },
  };
}

export class PipelineApiError extends Error {
  override readonly name = "PipelineApiError";

  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_CREWFSM_API_URL?.trim();
  return (
    configured ||
    (import.meta.env.PROD
      ? "https://api.crewfsm.com/api/v1"
      : "http://localhost:4100/api/v1")
  ).replace(/\/$/, "");
}
